import * as React from 'react'

import { clsx } from 'clsx'

import { useIsSplitEnabled } from '@buffer-mono/features'
import {
  Button,
  ChevronDownIcon,
  DropdownMenu,
  Flex,
  IconButton,
} from '@buffer-mono/popcorn'

import { COMPOSER_SHORTCUTS } from '~publish/constants/composerShortcuts'
import { wait } from '~publish/helpers/wait'
import { useDefaultScheduleOption } from '~publish/hooks/useDefaultScheduleOption'
import { useKeyboardShortcut } from '~publish/hooks/useKeyboardShortcut'
import { usePostComposer } from '~publish/hooks/usePostComposer'
import { useSelectedProfiles } from '~publish/hooks/useSelectedProfiles'
import type { UpdateCallback } from '~publish/legacy/composer/interfaces/types'

import { useSaveCallbacks } from '../../utils/useSaveCallbacks'
import { ConditionalTooltip } from '../ConditionalTooltip'
import { ScheduleSelector } from './schedule-selector/ScheduleSelector'
import type { ScheduleSelectorState } from './schedule-selector/types'
import { useScheduleSelector } from './schedule-selector/useScheduleSelector'
import { useComposerMode } from './useComposerMode'
import { usePostValidation } from './usePostValidation'
import { OmniboxButtons } from '../update-saver/OmniboxButtons'

import styles from './ComposerFooter.module.css'

const CTA_STRINGS = {
  SAVE_AND_NEW: 'publish-composer-save-andNew-1',
} as const

type ComposerFooterProps = {
  timezone?: string
  initialScheduledAt?: number | null
}

/**
 * Generates button labels for post scheduling actions
 */
const getPostLabel = ({
  scheduleSelectorOptions,
  hasMultipleProfilesEnabled = false,
  includeNewPost = false,
}: {
  scheduleSelectorOptions: ScheduleSelectorState | null
  hasMultipleProfilesEnabled?: boolean
  includeNewPost?: boolean
}): string => {
  let label =
    scheduleSelectorOptions?.option === 'Now' ? 'Publish Now' : 'Schedule Post'

  if (hasMultipleProfilesEnabled) {
    label = label.replace('Post', 'Posts')
  }

  return includeNewPost ? `${label} + Create Another` : label
}

export const ComposerFooter: React.FC<ComposerFooterProps> = ({
  timezone,
  initialScheduledAt,
}) => {
  const composerMode = useComposerMode()

  const defaultScheduleOption = useDefaultScheduleOption()
  const {
    isSavePostDisabled,
    isSaveDraftDisabled,
    postValidationIssues,
    draftValidationIssues,
  } = usePostValidation()

  const {
    isOmniboxEnabled,
    hasMultipleProfilesEnabled,
    isApprovalRequired,
    isSaveAndRequestApprovalDisabled,
    selectedProfiles,
  } = useSelectedProfiles()

  const {
    options,
    scheduleSelectorValue,
    onScheduleSelectorChange,
    isScheduleSelectionValid,
    fallbackOption,
  } = useScheduleSelector({
    initialScheduledAt,
    isApprovalRequired,
    defaultOption: defaultScheduleOption,
    composerMode,
  })

  const { schedulePost, saveAsDraft, saveAndRequestApproval, updatePost } =
    useSaveCallbacks(
      scheduleSelectorValue.option,
      scheduleSelectorValue.scheduledAt,
    )

  const { createNewPostInComposer } = usePostComposer()

  const [isSavingPost, setIsSavingPost] = React.useState(false)
  const [isSavingDraft, setIsSavingDraft] = React.useState(false)

  const isNewPostMode = composerMode === 'new-post'
  const isEditPostMode = composerMode === 'edit-post'
  const isEditDraftMode = composerMode === 'edit-draft'

  const handleSchedulePost = React.useCallback((): void => {
    if (isSavePostDisabled) return
    setIsSavingPost(true)
    void schedulePost().finally(() => {
      setIsSavingPost(false)
    })
  }, [schedulePost, isSavePostDisabled, setIsSavingPost])

  const schedulePostAndCreateNewPost = React.useCallback((): void => {
    if (isSavePostDisabled) return

    setIsSavingPost(true)
    void schedulePost({ willCreateAnotherPost: true })
      .then(() => wait(10))
      .then(() =>
        createNewPostInComposer({
          channels: selectedProfiles.map((profile) => profile.id),
          cta: CTA_STRINGS.SAVE_AND_NEW,
        }),
      )
      .finally(() => {
        setIsSavingPost(false)
      })
  }, [
    schedulePost,
    createNewPostInComposer,
    selectedProfiles,
    setIsSavingPost,
    isSavePostDisabled,
  ])

  const handleSaveDraft = React.useCallback((): void => {
    if (isSaveDraftDisabled) return
    setIsSavingDraft(true)
    void saveAsDraft().finally(() => {
      setIsSavingDraft(false)
    })
  }, [saveAsDraft, isSaveDraftDisabled])

  const handleSaveAndRequestApproval = React.useCallback((): void => {
    if (isSaveAndRequestApprovalDisabled) return
    setIsSavingDraft(true)
    void saveAndRequestApproval().finally(() => {
      setIsSavingDraft(false)
    })
  }, [saveAndRequestApproval, isSaveAndRequestApprovalDisabled])

  // Register keyboard shortcuts
  useKeyboardShortcut(COMPOSER_SHORTCUTS.SAVE_DRAFT, handleSaveDraft, {
    disabled: isSaveDraftDisabled,
    ignoreInputFocus: false,
  })
  useKeyboardShortcut(COMPOSER_SHORTCUTS.SCHEDULE_POST, handleSchedulePost, {
    disabled: isSavePostDisabled || isApprovalRequired,
    ignoreInputFocus: false,
  })
  useKeyboardShortcut(
    COMPOSER_SHORTCUTS.SCHEDULE_POST_AND_CREATE_NEW,
    schedulePostAndCreateNewPost,
    {
      disabled: isSavePostDisabled || isApprovalRequired,
      ignoreInputFocus: false,
    },
  )

  const draftLabel = hasMultipleProfilesEnabled
    ? 'Save as Drafts'
    : 'Save as Draft'

  const postLabel = getPostLabel({
    scheduleSelectorOptions: scheduleSelectorValue,
    hasMultipleProfilesEnabled,
  })

  const requestApprovalIssues = isSaveAndRequestApprovalDisabled
    ? `Some of your selected channels do not require approval. Use "${draftLabel}" first and you can request approval later if needed.`
    : draftValidationIssues

  const scheduleSelectorIssues = isScheduleSelectionValid
    ? null
    : 'Please select a time to schedule your post'

  return (
    <Flex
      className={styles.composerFooter}
      as="section"
      data-testid="composer-footer"
      justify="end"
      direction="row"
      gap="sm"
      align="center"
    >
      <ScheduleSelector
        value={scheduleSelectorValue}
        onChange={onScheduleSelectorChange}
        options={options}
        timezone={timezone}
        className={styles.scheduleSelector}
        defaultOption={defaultScheduleOption}
        fallbackOption={fallbackOption}
        channelIdForSlots={
          !hasMultipleProfilesEnabled ? selectedProfiles[0]?.id : undefined
        }
      />

      {isOmniboxEnabled && isNewPostMode && <OmniboxButtons />}

      <SaveAsDraftButton
        show={!isOmniboxEnabled && isNewPostMode}
        label={draftLabel}
        tooltipContent={scheduleSelectorIssues || draftValidationIssues}
        disabled={!isScheduleSelectionValid || isSaveDraftDisabled}
        loading={isSavingDraft}
        onClick={handleSaveDraft}
      />

      <SaveAndRequestApprovalButton
        show={!isOmniboxEnabled && isApprovalRequired && isNewPostMode}
        tooltipContent={scheduleSelectorIssues || requestApprovalIssues}
        disabled={isSaveDraftDisabled || isSaveAndRequestApprovalDisabled}
        onClick={handleSaveAndRequestApproval}
        loading={isSavingDraft}
      />

      <SchedulePostButton
        label={postLabel}
        show={!isOmniboxEnabled && !isApprovalRequired && isNewPostMode}
        tooltipContent={scheduleSelectorIssues || postValidationIssues}
        disabled={!isScheduleSelectionValid || isSavePostDisabled}
        loading={isSavingPost}
        schedulePost={handleSchedulePost}
        scheduleSelectorOptions={scheduleSelectorValue}
        schedulePostAndCreateNewPost={schedulePostAndCreateNewPost}
      />

      <EditPostButtons
        show={isEditPostMode}
        updateCallback={updatePost}
        draftValidationIssues={draftValidationIssues}
        isSaveDraftDisabled={isSaveDraftDisabled}
        isSavingDraft={isSavingDraft}
        scheduleSelectorIssues={scheduleSelectorIssues}
        postValidationIssues={postValidationIssues}
        isScheduleSelectionValid={isScheduleSelectionValid}
        isSavePostDisabled={isSavePostDisabled}
        isSavingPost={isSavingPost}
      />

      <EditDraftButtons
        show={isEditDraftMode}
        updateCallback={updatePost}
        draftValidationIssues={draftValidationIssues}
        isSaveDraftDisabled={isSaveDraftDisabled}
        isSavingDraft={isSavingDraft}
        scheduleSelectorIssues={scheduleSelectorIssues}
        postValidationIssues={postValidationIssues}
        isScheduleSelectionValid={isScheduleSelectionValid}
        isSavePostDisabled={isSavePostDisabled}
        isSavingPost={isSavingPost}
        scheduleSelectorOptions={scheduleSelectorValue}
      />
    </Flex>
  )
}

const SaveAsDraftButton = ({
  show,
  tooltipContent,
  label,
  disabled,
  onClick,
  loading,
}: {
  show: boolean
  tooltipContent: string | Array<React.ReactElement | string> | null
  label: string
  disabled: boolean
  onClick: () => void
  loading: boolean
}): JSX.Element | null => {
  if (!show) return null

  return (
    <ConditionalTooltip content={tooltipContent}>
      <Button
        variant="secondary"
        size="large"
        disabled={disabled}
        onClick={onClick}
        loading={loading}
        className={styles.saveDraftButton}
        aria-keyshortcuts={COMPOSER_SHORTCUTS.SAVE_DRAFT}
      >
        {label}
      </Button>
    </ConditionalTooltip>
  )
}

const EditDraftButtons = ({
  show,
  updateCallback,
  draftValidationIssues,
  isSaveDraftDisabled,
  isSavingDraft,
  postValidationIssues,
  isScheduleSelectionValid,
  isSavePostDisabled,
  isSavingPost,
  scheduleSelectorIssues,
}: {
  show: boolean
  updateCallback: UpdateCallback
  draftValidationIssues: string | Array<React.ReactElement | string> | null
  isSaveDraftDisabled: boolean
  isSavingDraft: boolean
  scheduleSelectorIssues: string | Array<React.ReactElement | string> | null
  postValidationIssues: string | Array<React.ReactElement | string> | null
  isScheduleSelectionValid: boolean
  isSavePostDisabled: boolean
  isSavingPost: boolean
  scheduleSelectorOptions: ScheduleSelectorState | null
}): JSX.Element | null => {
  const showSchedulePost = useIsSplitEnabled(
    'core-ux-composer-footer-draft-to-post-action',
  )

  const saveDraft = React.useCallback(() => {
    void updateCallback({ currentStatus: 'draft' })
  }, [updateCallback])

  const schedulePost = React.useCallback(() => {
    void updateCallback({ currentStatus: 'draft', newStatus: 'post' })
  }, [updateCallback])

  if (!show) return null

  return (
    <>
      <EditSaveButton
        show={true}
        variant="secondary"
        label="Save Draft"
        onClick={saveDraft}
        tooltipContent={draftValidationIssues}
        disabled={isSaveDraftDisabled}
        loading={isSavingDraft}
      />

      {showSchedulePost && (
        <ConditionalTooltip
          content={scheduleSelectorIssues || postValidationIssues}
        >
          <Button
            variant="primary"
            size="large"
            disabled={!isScheduleSelectionValid || isSavePostDisabled}
            onClick={schedulePost}
            loading={isSavingPost}
            className={styles.schedulePostButton}
            aria-keyshortcuts={COMPOSER_SHORTCUTS.SCHEDULE_POST}
          >
            Schedule Post
          </Button>
        </ConditionalTooltip>
      )}
    </>
  )
}

const EditPostButtons = ({
  show,
  updateCallback,
  draftValidationIssues,
  isSaveDraftDisabled,
  isSavingDraft,
  postValidationIssues,
  isScheduleSelectionValid,
  isSavePostDisabled,
  isSavingPost,
  scheduleSelectorIssues,
}: {
  show: boolean
  updateCallback: UpdateCallback
  draftValidationIssues: string | Array<React.ReactElement | string> | null
  isSaveDraftDisabled: boolean
  isSavingDraft: boolean
  postValidationIssues: string | Array<React.ReactElement | string> | null
  isScheduleSelectionValid: boolean
  isSavePostDisabled: boolean
  isSavingPost: boolean
  scheduleSelectorIssues: string | Array<React.ReactElement | string> | null
}): JSX.Element | null => {
  const showMoveToDraft = useIsSplitEnabled(
    'core-ux-composer-footer-move-to-drafts-action',
  )
  const savePost = React.useCallback(() => {
    void updateCallback({ currentStatus: 'post' })
  }, [updateCallback])

  const moveToDraft = React.useCallback(() => {
    void updateCallback({ currentStatus: 'post', newStatus: 'draft' })
  }, [updateCallback])

  if (!show) return null

  return (
    <>
      {showMoveToDraft && (
        <ConditionalTooltip content={draftValidationIssues}>
          <Button
            variant="tertiary"
            size="large"
            disabled={isSaveDraftDisabled}
            onClick={moveToDraft}
            loading={isSavingDraft}
          >
            Move to Drafts
          </Button>
        </ConditionalTooltip>
      )}

      <ConditionalTooltip
        content={scheduleSelectorIssues || postValidationIssues}
      >
        {/* Update Post or Draft button */}
        <Button
          variant="primary"
          size="large"
          disabled={!isScheduleSelectionValid || isSavePostDisabled}
          onClick={savePost}
          loading={isSavingPost}
          aria-keyshortcuts={COMPOSER_SHORTCUTS.UPDATE_POST}
        >
          Save
        </Button>
      </ConditionalTooltip>
    </>
  )
}

const SaveAndRequestApprovalButton = ({
  tooltipContent,
  disabled,
  onClick,
  loading,
  show,
}: {
  tooltipContent: string | Array<React.ReactElement | string> | null
  disabled: boolean
  onClick: () => void
  loading: boolean
  show: boolean
}): JSX.Element | null => {
  if (!show) return null

  return (
    // Save and Request Approval button
    <ConditionalTooltip content={tooltipContent}>
      <Button
        variant="primary"
        size="large"
        disabled={disabled}
        onClick={onClick}
        loading={loading}
      >
        Save and Request Approval
      </Button>
    </ConditionalTooltip>
  )
}

const SchedulePostButton = ({
  label,
  tooltipContent,
  disabled,
  schedulePost,
  loading,
  show,
  scheduleSelectorOptions,
  schedulePostAndCreateNewPost,
}: {
  label: string
  tooltipContent: string | Array<React.ReactElement | string> | null
  disabled: boolean
  schedulePost: () => void
  loading: boolean
  show: boolean
  scheduleSelectorOptions: ScheduleSelectorState | null
  schedulePostAndCreateNewPost: () => void
}): JSX.Element | null => {
  if (!show) return null

  return (
    <ConditionalTooltip content={tooltipContent}>
      <Button.Group variant="primary">
        {/* Schedule Post button */}
        <Button
          variant="primary"
          size="large"
          disabled={disabled}
          onClick={schedulePost}
          loading={loading}
          className={styles.schedulePostButton}
          aria-keyshortcuts={COMPOSER_SHORTCUTS.SCHEDULE_POST}
        >
          {label}
        </Button>

        {/* Schedule Post + New Post button */}
        <ScheduleMoreActions
          scheduleSelectorOptions={scheduleSelectorOptions}
          isSavePostDisabled={disabled}
          isSavingPost={loading}
          onScheduleAndCreateNew={schedulePostAndCreateNewPost}
        />
      </Button.Group>
    </ConditionalTooltip>
  )
}

const ScheduleMoreActions = ({
  scheduleSelectorOptions,
  isSavePostDisabled,
  isSavingPost,
  onScheduleAndCreateNew,
}: {
  scheduleSelectorOptions: ScheduleSelectorState | null
  isSavePostDisabled: boolean
  isSavingPost: boolean
  onScheduleAndCreateNew: () => void
}): JSX.Element => {
  const postLabelForMoreActions = getPostLabel({
    scheduleSelectorOptions,
    includeNewPost: true,
  })

  const [open, setOpen] = React.useState(false)
  const handleEscapeKeyDown = React.useCallback(
    (e: KeyboardEvent) => {
      // Manually handle Escape key press when the dropdown is open.
      // Although Popcorn/Radix DropdownMenu typically handles Escape,
      // the legacy composer structure and its own Escape key listeners
      // require explicit prevention of default behavior and stopping
      // event propagation here to prevent the main composer from closing.
      if (open) {
        e.preventDefault()
        e.stopPropagation()
        setOpen(false)
      }
    },
    [open],
  )

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenu.Trigger disabled={isSavePostDisabled}>
        <IconButton
          label="more options"
          size="large"
          disabled={!isSavingPost && isSavePostDisabled}
          className={clsx(styles.scheduleMoreActionsButton, {
            [styles.loading]: isSavingPost,
          })}
        >
          <ChevronDownIcon />
        </IconButton>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content
        side="top"
        sideOffset={10}
        className={styles.scheduleMoreActionsDropdown}
        onEscapeKeyDown={handleEscapeKeyDown}
      >
        <DropdownMenu.Item
          onClick={onScheduleAndCreateNew}
          shortcut={COMPOSER_SHORTCUTS.SCHEDULE_POST_AND_CREATE_NEW}
          aria-keyshortcuts={COMPOSER_SHORTCUTS.SCHEDULE_POST_AND_CREATE_NEW}
        >
          {postLabelForMoreActions}
        </DropdownMenu.Item>
      </DropdownMenu.Content>
    </DropdownMenu>
  )
}

const EditSaveButton = ({
  label,
  tooltipContent,
  disabled,
  onClick,
  loading,
  show,
  variant = 'primary',
}: {
  label: string
  tooltipContent: string | Array<React.ReactElement | string> | null
  disabled: boolean
  onClick: () => void
  loading: boolean
  show: boolean
  variant?: 'primary' | 'secondary'
}): JSX.Element | null => {
  if (!show) return null

  return (
    <ConditionalTooltip content={tooltipContent}>
      {/* Update Post or Draft button */}
      <Button
        variant={variant}
        size="large"
        disabled={disabled}
        onClick={onClick}
        loading={loading}
        aria-keyshortcuts={COMPOSER_SHORTCUTS.UPDATE_POST}
      >
        {label}
      </Button>
    </ConditionalTooltip>
  )
}
